import { FormControl } from '@angular/forms';

import { StringUtils } from 'tiime-expert-utils';

import { SelectElementForm } from '@forms/select-element-form/select-element-form';
import { SelectElementsFormArray } from '@forms/select-elements-form-array/select-elements-form-array';
import { PcgEntryGroupedByRoot } from '@interfaces/pcg-entry-grouped-by-root';
import { PcgEntry } from '@models/pcg-entry';

enum SortScore {
  NO_MATCH = 0,
  LABEL_CONTAINS = 1,
  LABEL_START = 2,
  ACCOUNT_CONTAINS = 3,
  ACCOUNT_START = 4
}

export class PcgEntryHelper {
  static resetAccountNumber(pcgEntryId: number, pcgEntryAccountNumber: string, pcgEntries: PcgEntry[] = []): string {
    if (!pcgEntryId) {
      pcgEntryAccountNumber = '';
    } else {
      const pcgEntry = (pcgEntries ?? []).find((pE: PcgEntry) => pE.id === pcgEntryId);
      if (pcgEntry && pcgEntry.accountNumber !== pcgEntryAccountNumber) {
        pcgEntryAccountNumber = pcgEntry.accountNumber;
      }
    }
    return pcgEntryAccountNumber;
  }

  static generateThirdPartyAccountNumber(
    categoryAccountNumber: string,
    pcgEntries: PcgEntry[] = [],
    purchasesAccountNumberPrefix: string,
    salesAccountNumberPrefix: string
  ): string {
    const ACCOUNT_NUMBER_LENGTH = 8;
    const NUMERIC_REGEX = /^\d+$/;
    const PURCHASE_START_WITH = ['2', '60', '61', '62', '63', '65'];
    const SALES_START_WITH = ['70'];
    const existingAccountNumbers: string[] = pcgEntries.map((pcgEntry: PcgEntry) => pcgEntry.accountNumber);

    let thirdPartyAccountNumberPrefix = null;

    if (PURCHASE_START_WITH.some((value: string) => categoryAccountNumber.startsWith(value))) {
      thirdPartyAccountNumberPrefix = purchasesAccountNumberPrefix;
    }

    if (SALES_START_WITH.some((value: string) => categoryAccountNumber.startsWith(value))) {
      thirdPartyAccountNumberPrefix = salesAccountNumberPrefix;
    }

    if (!thirdPartyAccountNumberPrefix) {
      return null;
    }

    let thirdPartyAccountNumber = null;

    for (let i = 1; i <= Number('9'.repeat(ACCOUNT_NUMBER_LENGTH - thirdPartyAccountNumberPrefix.length)); i++) {
      if (NUMERIC_REGEX.test(thirdPartyAccountNumberPrefix)) {
        thirdPartyAccountNumber = `${thirdPartyAccountNumberPrefix}${i}`.padEnd(ACCOUNT_NUMBER_LENGTH, '0');
      } else {
        thirdPartyAccountNumber = `${thirdPartyAccountNumberPrefix}${i}`;
      }

      if (!existingAccountNumbers.includes(thirdPartyAccountNumber)) {
        return thirdPartyAccountNumber;
      }
    }

    return null;
  }

  /**
   * Applique un filtre et tri les pcgEntries selon les priorités suivantes:
   * - Le numéro de compte commence par le filtre
   * - Le numéro de compte contient le filtre
   * - Le label du compte commence par le filtre
   * - Le label du compte contient le filtre
   * Si deux éléments ont la même priorité, on applique un tri alphabétique
   */
  static filterPcgEntries(filter: string, pcgEntries: PcgEntry[] = []): PcgEntry[] {
    if (!filter) {
      return pcgEntries ?? [];
    }

    return PcgEntryHelper.sortWithFilter(
      filter,
      (pcgEntries ?? []).filter(
        (pcgEntry: PcgEntry) =>
          StringUtils.normalizeToLowerCase(pcgEntry.accountNumber).includes(StringUtils.normalizeToLowerCase(filter)) ||
          StringUtils.normalizeToLowerCase(pcgEntry.label).includes(StringUtils.normalizeToLowerCase(filter))
      )
    );
  }

  /**
   * Tri les pcgEntries selon les priorités suivantes:
   * - Le numéro de compte commence par le filtre
   * - Le numéro de compte contient le filtre
   * - Le label du compte commence par le filtre
   * - Le label du compte contient le filtre
   * Si deux éléments ont la même priorité, on applique un tri alphabétique
   */
  static sortWithFilter(filter: string, pcgEntries: PcgEntry[] = []): PcgEntry[] {
    if (!filter) {
      return pcgEntries;
    }

    const filterLowercase = StringUtils.normalizeToLowerCase(filter);

    /**
     *  On calcul un score sur chaque pcgEntry pour pouvoir trier facilement:
     *  4 - Le numéro de compte commence par la recherche
     *  3 - Le numéro de compte contient la recherche
     *  2 - Le label commence par la recherche
     *  1 - Le label contient la recherche
     *  0 - Ne contient pas la recherche
     */
    function getScore(pcgEntry: PcgEntry): SortScore {
      if (StringUtils.normalizeToLowerCase(pcgEntry.accountNumber).startsWith(filterLowercase)) {
        return SortScore.ACCOUNT_START;
      }
      if (StringUtils.normalizeToLowerCase(pcgEntry.accountNumber).includes(filterLowercase)) {
        return SortScore.ACCOUNT_CONTAINS;
      }
      if (StringUtils.normalizeToLowerCase(pcgEntry.label).startsWith(filterLowercase)) {
        return SortScore.LABEL_START;
      }
      if (StringUtils.normalizeToLowerCase(pcgEntry.label).includes(filterLowercase)) {
        return SortScore.LABEL_CONTAINS;
      }
      return SortScore.NO_MATCH;
    }

    return pcgEntries.sort((a, b) => {
      const scoreA = getScore(a);
      const scoreB = getScore(b);
      // Dans le cas où on a le même score, on applique un tri alphabétique
      if (scoreA === scoreB) {
        let field: 'accountNumber' | 'label';
        // Si on est sur une priorité sur le numéro de compte
        if ([SortScore.ACCOUNT_CONTAINS, SortScore.ACCOUNT_START, SortScore.NO_MATCH].includes(scoreA)) {
          field = 'accountNumber';
        } else {
          // Si on est sur une priorité sur le label du compte
          field = 'label';
        }

        return StringUtils.normalizeToLowerCase(a[field]).localeCompare(StringUtils.normalizeToLowerCase(b[field]));
      }
      return scoreB - scoreA;
    });
  }

  /**
   * Vérifie si le numéro de compte correspond à un numéro de compte auxiliaire
   *
   * @param accountNumber
   */
  static isAuxiliaryAccountNumber(accountNumber: string): boolean {
    if (!accountNumber?.length) {
      return false;
    }
    return /^(?!4).*[a-z].*/gi.test(accountNumber);
  }

  /**
   * Tri les comptes sur le numéro de compte
   *
   * @param pcgEntries
   */
  static sortOnAccountNumber(pcgEntries: PcgEntry[]): PcgEntry[] {
    return [...pcgEntries].sort((currentPcgEntry: PcgEntry, nextPcgEntry: PcgEntry) =>
      currentPcgEntry.accountNumber.localeCompare(nextPcgEntry.accountNumber)
    );
  }

  static groupByRoot(
    pcgEntries: PcgEntry[],
    control: SelectElementsFormArray<PcgEntry>,
    selectedByDefault: (pcgEntry: PcgEntry) => boolean
  ): PcgEntryGroupedByRoot[] {
    control.clear();

    return pcgEntries.reduce((data: PcgEntryGroupedByRoot[], pcgEntry: PcgEntry): PcgEntryGroupedByRoot[] => {
      const pcgEntryNeedToBeSelected: boolean = selectedByDefault(pcgEntry);

      const form: SelectElementForm<PcgEntry> = new SelectElementForm<PcgEntry>();
      form.fromValue({ ...pcgEntry, selected: pcgEntryNeedToBeSelected });

      const pcgEntryByRoot: PcgEntryGroupedByRoot = data.find((pcgEntryRoot: PcgEntryGroupedByRoot) =>
        pcgEntryRoot.accountNumberRoot.startsWith(
          !pcgEntry.centralizerPcgEntry
            ? pcgEntry.accountNumber.substring(0, 2)
            : pcgEntry.centralizerPcgEntry.accountNumber.substring(0, 2)
        )
      );

      if (!pcgEntryByRoot) {
        const accountNumbers: SelectElementsFormArray<PcgEntry> = new SelectElementsFormArray<PcgEntry>();
        accountNumbers.push(form);
        data.push({
          accountNumberRoot: pcgEntry.accountNumber.substring(0, 2).concat('xxxx'),
          selected: new FormControl(pcgEntryNeedToBeSelected),
          pcgEntries: accountNumbers
        });
      } else {
        // if the root pcg entry is not currently selected and the pcg entry need to be selected
        // then selected it
        if (!pcgEntryByRoot.selected.value && pcgEntryNeedToBeSelected) {
          pcgEntryByRoot.selected.patchValue(true);
        }
        pcgEntryByRoot.pcgEntries.push(form);
      }
      control.push(form);

      return data;
    }, []);
  }
}
